import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Link } from 'react-router-dom';
import { authFetch } from '../../utils/authFetch';
import {
  Users, Search, Bell, ArrowRight, AlertTriangle, Clock,
  TrendingUp, Zap, Star, X, Layers
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, AreaChart, Area, Cell
} from 'recharts';
import PageHelp from '../../components/PageHelp';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const STAGE_LABELS = {
  saved:        'Saved',
  contacted:    'Interest Sent',
  interest_sent:'Interest Sent',
  accepted:     'Accepted',
  in_process:   'In Process',
  unavailable:  'Unavailable',
  hired:        'Hired',
  archived:     'Archived',
};
const STAGE_COLORS = {
  saved:       '#3b82f6',
  contacted:   '#f59e0b',
  accepted:    '#22c55e',
  in_process:  '#8b5cf6',
  unavailable: '#ef4444',
  hired:       '#c9a84c',
  archived:    '#9ca3af',
};

const getDaysLeft = (expiresAt) => {
  if (!expiresAt) return null;
  return Math.floor((new Date(expiresAt) - new Date()) / 86400000);
};

// ─── Metric Card ─────────────────────────────────────────────────────────────
const MetricCard = ({ label, value, icon, link, accent }) => (
  <Link
    to={link}
    className="bg-white border border-border p-5 block hover:border-foreground/20"
    style={{ transition: 'border-color 150ms' }}
    data-testid={`metric-card-${label.toLowerCase().replace(/\s+/g, '-')}`}
  >
    <div className="flex items-center justify-between mb-3">
      <span style={{ color: accent || '#9ca3af' }}>{icon}</span>
      <ArrowRight className="w-3.5 h-3.5 text-muted-foreground/40" strokeWidth={1.5} />
    </div>
    <p
      className="text-3xl font-bold text-foreground"
      style={{ fontFamily: "'Bodoni Moda', Georgia, serif" }}
    >
      {value ?? '—'}
    </p>
    <p
      className="text-xs text-muted-foreground mt-1 uppercase tracking-widest"
      style={{ fontFamily: "'IBM Plex Mono', monospace" }}
    >
      {label}
    </p>
  </Link>
);

// ─── Custom Tooltip ───────────────────────────────────────────────────────────
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload?.length) {
    return (
      <div className="bg-white border border-border px-3 py-2 shadow-sm">
        <p className="text-xs font-semibold mb-1">{label}</p>
        {payload.map((p, i) => (
          <p key={i} className="text-xs" style={{ color: p.color, fontFamily: "'IBM Plex Mono', monospace" }}>
            {p.name}: {p.value}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

// ─── Welcome Banner (first visit) ────────────────────────────────────────────
const WELCOME_KEY = 'fs_onboarded_v1';

const WelcomeBanner = () => {
  const [visible, setVisible] = useState(() => !localStorage.getItem(WELCOME_KEY));
  if (!visible) return null;

  const dismiss = () => {
    localStorage.setItem(WELCOME_KEY, '1');
    setVisible(false);
  };

  return (
    <div className="bg-[#111] text-white p-6 flex items-start gap-5 relative" data-testid="welcome-banner">
      <div className="flex-shrink-0 w-10 h-10 border border-white/10 flex items-center justify-center">
        <Layers className="w-5 h-5 text-white/60" strokeWidth={1.5} />
      </div>
      <div className="flex-1 min-w-0">
        <p style={{ fontFamily: "'Bodoni Moda', Georgia, serif", fontSize: '1rem' }}
          className="text-white font-medium mb-1">Welcome to FashionSpyder</p>
        <p className="text-white/50 text-xs leading-relaxed mb-4">
          Your AI-powered HR intelligence platform for the fashion industry.
          Start by browsing the gallery, saving candidates to your pipeline, or running the AI Matchmaker.
        </p>
        <div className="flex gap-2 flex-wrap">
          {[
            { label: 'Browse Gallery', to: '/gallery' },
            { label: 'Try AI Matchmaker', to: '/matchmaker' },
            { label: 'Set Hiring Focus', to: '/dashboard/account' },
          ].map(({ label, to }) => (
            <Link key={label} to={to}
              className="text-xs border border-white/15 px-3 py-1.5 text-white/60 hover:text-white hover:border-white/30 transition-colors"
              style={{ fontFamily: "'IBM Plex Mono', monospace", letterSpacing: '0.05em' }}>
              {label} →
            </Link>
          ))}
        </div>
      </div>
      <button onClick={dismiss}
        className="text-white/20 hover:text-white/60 flex-shrink-0 transition-colors"
        data-testid="welcome-banner-dismiss">
        <X className="w-4 h-4" strokeWidth={1.5} />
      </button>
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const DashboardOverview = ({ stats }) => {
  const { user } = useAuth();
  const [analytics, setAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(true);

  useEffect(() => {
    authFetch(`${BACKEND}/api/dashboard/analytics`, {})
      .then(r => r.json())
      .then(d => { if (d.success) setAnalytics(d.analytics); })
      .catch(() => {})
      .finally(() => setAnalyticsLoading(false));
  }, []);

  const metricCards = [
    {
      label: 'Total Pipeline',
      value: stats.total_pipeline,
      icon: <Users className="w-4 h-4" strokeWidth={1.5} />,
      link: '/dashboard/pipeline',
      accent: '#3b82f6',
    },
    {
      label: 'Hired',
      value: analytics?.hired_count ?? 0,
      icon: <Star className="w-4 h-4" strokeWidth={1.5} />,
      link: '/dashboard/pipeline',
      accent: '#c9a84c',
    },
    {
      label: 'AI Matches Run',
      value: analytics?.ai_matches_run ?? 0,
      icon: <Zap className="w-4 h-4" strokeWidth={1.5} />,
      link: '/dashboard/pipeline',
      accent: '#8b5cf6',
    },
    {
      label: 'Talent Briefs',
      value: stats.saved_searches,
      icon: <Search className="w-4 h-4" strokeWidth={1.5} />,
      link: '/dashboard/searches',
      accent: '#10b981',
    },
  ];

  // Build funnel chart data
  const funnelData = ['saved', 'contacted', 'accepted', 'in_process', 'hired'].map(stage => ({
    stage: STAGE_LABELS[stage],
    count: (analytics?.pipeline_by_stage?.[stage] ?? 0) + (stage === 'contacted' ? (analytics?.pipeline_by_stage?.['interest_sent'] ?? 0) : 0),
    fill: STAGE_COLORS[stage],
  }));

  // Build monthly trend data
  const monthlyData = analytics?.monthly_additions
    ? Object.entries(analytics.monthly_additions)
        .sort(([a], [b]) => new Date(a) - new Date(b))
        .slice(-6)
        .map(([month, count]) => ({ month: month.replace(' 2026', '').replace(' 2025', ''), count }))
    : [];

  return (
    <div className="space-y-5 sm:space-y-8">
      {/* First-visit welcome banner */}
      <WelcomeBanner />

      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1
            className="text-xl sm:text-2xl font-semibold text-foreground"
            style={{ fontFamily: "'Bodoni Moda', Georgia, serif" }}
            data-testid="dashboard-greeting"
          >
            Good {new Date().getHours() < 12 ? 'morning' : new Date().getHours() < 17 ? 'afternoon' : 'evening'},{' '}
            {user?.full_name?.split(' ')[0]}
          </h1>
          <p
            className="text-muted-foreground text-xs mt-1 uppercase tracking-widest"
            style={{ fontFamily: "'IBM Plex Mono', monospace" }}
          >
            {user?.company_name} · HR Intelligence Dashboard
          </p>
        </div>
        <PageHelp pageKey="overview" />
      </div>

      {/* Hiring focus nudge — when preferred_roles not set */}
      {user?.subscription_plan !== 'free' && !(user?.preferred_roles?.length > 0) && (
        <div className="border border-border bg-white p-4 flex items-center justify-between gap-4" data-testid="focus-nudge">
          <div className="flex items-start gap-3">
            <div className="w-1.5 h-1.5 bg-amber-400 rounded-full mt-1.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-semibold">Set your hiring focus</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                Add target roles and locations to see personalised match signals in the gallery.
              </p>
            </div>
          </div>
          <Link to="/dashboard/account" className="text-xs text-foreground border border-border px-3 py-1.5 hover:bg-secondary whitespace-nowrap flex-shrink-0">
            Set focus →
          </Link>
        </div>
      )}

      {/* Plan upgrade banner */}
      {user?.subscription_plan === 'free' && (
        <div className="bg-[#0a0a0a] text-white p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3" data-testid="upgrade-banner">
          <div>
            <p className="font-semibold text-sm">Unlock Full Intelligence</p>
            <p className="text-white/60 text-xs mt-0.5">
          Access verified fashion professionals and the AI DNA Matchmaker.
            </p>
          </div>
          <Link
            to="/pricing"
            className="flex items-center gap-2 text-xs bg-white text-black px-4 py-2 hover:bg-white/90 self-start sm:self-auto flex-shrink-0"
            style={{ transition: 'background-color 150ms' }}
          >
            View Plans <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      )}

      {/* Subscription expiry warnings */}
      {user?.subscription_plan !== 'free' && user?.subscription_expires_at && (() => {
        const days = getDaysLeft(user.subscription_expires_at);
        if (days < 0) return (
          <div className="bg-red-50 border border-red-200 p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <div className="flex items-center gap-2 text-red-700">
              <AlertTriangle className="w-4 h-4 flex-shrink-0" strokeWidth={1.5} />
              <span className="text-sm font-medium">Your subscription has expired. Renew to restore access.</span>
            </div>
            <Link to="/dashboard/account" className="text-sm text-red-700 underline font-medium self-start sm:self-auto">Renew now →</Link>
          </div>
        );
        if (days <= 7) return (
          <div className="bg-yellow-50 border border-yellow-200 p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <div className="flex items-center gap-2 text-yellow-800">
              <Clock className="w-4 h-4 flex-shrink-0" strokeWidth={1.5} />
              <span className="text-sm font-medium">
                {days === 0 ? 'Expires today!' : `${days} day${days !== 1 ? 's' : ''} left on your ${user.subscription_plan.toUpperCase()} plan.`}
              </span>
            </div>
            <Link to="/dashboard/account" className="text-sm text-yellow-800 underline font-medium self-start sm:self-auto">Manage →</Link>
          </div>
        );
        return null;
      })()}

      {/* Metric cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="metrics-grid">
        {metricCards.map(card => (
          <MetricCard key={card.label} {...card} />
        ))}
      </div>

      {/* Charts row */}
      {!analyticsLoading && stats.total_pipeline > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
          {/* Pipeline funnel — wider */}
          <div className="lg:col-span-3 bg-white border border-border p-6" data-testid="pipeline-funnel-chart">
            <div className="flex items-center justify-between mb-5">
              <div>
                <h2 className="text-sm font-semibold">Hiring Funnel</h2>
                <p className="text-xs text-muted-foreground mt-0.5">Candidates by pipeline stage</p>
              </div>
              <Link
                to="/dashboard/pipeline"
                className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1"
              >
                View all <ArrowRight className="w-3 h-3" />
              </Link>
            </div>
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={funnelData} layout="vertical" margin={{ top: 0, right: 20, bottom: 0, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 10, fontFamily: "'IBM Plex Mono', monospace" }} axisLine={false} tickLine={false} />
                <YAxis type="category" dataKey="stage" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} width={72} />
                <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(0,0,0,0.03)' }} />
                <Bar dataKey="count" radius={0} maxBarSize={20} name="Candidates">
                  {funnelData.map((entry, i) => (
                    <Cell key={i} fill={entry.fill} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Stage breakdown — narrower */}
          <div className="lg:col-span-2 bg-white border border-border p-6" data-testid="stage-breakdown">
            <h2 className="text-sm font-semibold mb-4">Stage Breakdown</h2>
            <div className="space-y-3">
              {['saved', 'contacted', 'in_process', 'hired', 'archived'].map(stage => {
                const count = analytics?.pipeline_by_stage?.[stage] ?? 0;
                const total = stats.total_pipeline || 1;
                const pct = Math.round((count / total) * 100);
                return (
                  <div key={stage} data-testid={`stage-row-${stage}`}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-medium">{STAGE_LABELS[stage]}</span>
                      <span
                        className="text-xs text-muted-foreground"
                        style={{ fontFamily: "'IBM Plex Mono', monospace" }}
                      >
                        {count}
                      </span>
                    </div>
                    <div className="h-1 bg-secondary w-full">
                      <div
                        className="h-1"
                        style={{
                          width: `${pct}%`,
                          backgroundColor: STAGE_COLORS[stage],
                          transition: 'width 600ms ease-out',
                        }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Monthly trend chart */}
      {!analyticsLoading && monthlyData.length > 1 && (
        <div className="bg-white border border-border p-6" data-testid="monthly-trend-chart">
          <div className="flex items-center justify-between mb-5">
            <div>
              <h2 className="text-sm font-semibold">Monthly Pipeline Activity</h2>
              <p className="text-xs text-muted-foreground mt-0.5">Candidates added over time</p>
            </div>
            <TrendingUp className="w-4 h-4 text-muted-foreground/40" strokeWidth={1.5} />
          </div>
          <ResponsiveContainer width="100%" height={140}>
            <AreaChart data={monthlyData} margin={{ top: 4, right: 4, bottom: 0, left: -20 }}>
              <defs>
                <linearGradient id="monthlyGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#0a0a0a" stopOpacity={0.08} />
                  <stop offset="95%" stopColor="#0a0a0a" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
              <XAxis dataKey="month" tick={{ fontSize: 10, fontFamily: "'IBM Plex Mono', monospace" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 10, fontFamily: "'IBM Plex Mono', monospace" }} axisLine={false} tickLine={false} allowDecimals={false} />
              <Tooltip content={<CustomTooltip />} cursor={{ stroke: '#0a0a0a', strokeWidth: 1, strokeDasharray: '4 4' }} />
              <Area
                type="monotone"
                dataKey="count"
                name="Added"
                stroke="#0a0a0a"
                strokeWidth={1.5}
                fill="url(#monthlyGrad)"
                dot={{ fill: '#0a0a0a', r: 3, strokeWidth: 0 }}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Quick actions */}
      <div className="bg-white border border-border p-6">
        <h2 className="text-sm font-semibold mb-4 uppercase tracking-widest" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
          Quick Actions
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <Link
            to="/gallery"
            className="flex items-center gap-3 p-4 border border-border hover:border-foreground/20 hover:bg-secondary/30"
            style={{ transition: 'border-color 150ms, background-color 150ms' }}
            data-testid="quick-action-gallery"
          >
            <Users className="w-4 h-4 text-muted-foreground flex-shrink-0" strokeWidth={1.5} />
            <div>
              <p className="text-xs font-semibold">Browse Gallery</p>
              <p className="text-xs text-muted-foreground mt-0.5">
              {analytics?.total_candidates_available
                ? `${analytics.total_candidates_available} verified professionals`
                : 'Verified professionals'}
              </p>
            </div>
          </Link>
          <Link
            to="/ai-matchmaker"
            className="flex items-center gap-3 p-4 border border-border hover:border-foreground/20 hover:bg-secondary/30"
            style={{ transition: 'border-color 150ms, background-color 150ms' }}
            data-testid="quick-action-matchmaker"
          >
            <Zap className="w-4 h-4 text-muted-foreground flex-shrink-0" strokeWidth={1.5} />
            <div>
              <p className="text-xs font-semibold">AI Matchmaker</p>
              <p className="text-xs text-muted-foreground mt-0.5">Paste a JD, get ranked talent</p>
            </div>
          </Link>
          <Link
            to="/dashboard/searches"
            className="flex items-center gap-3 p-4 border border-border hover:border-foreground/20 hover:bg-secondary/30"
            style={{ transition: 'border-color 150ms, background-color 150ms' }}
            data-testid="quick-action-briefs"
          >
            <Search className="w-4 h-4 text-muted-foreground flex-shrink-0" strokeWidth={1.5} />
            <div>
              <p className="text-xs font-semibold">Create Talent Brief</p>
              <p className="text-xs text-muted-foreground mt-0.5">Set up automated match alerts</p>
            </div>
          </Link>
        </div>
      </div>

      {/* Notifications badge */}
      {stats.unread_notifications > 0 && (
        <div className="bg-white border border-border p-4 flex items-center justify-between" data-testid="notifications-banner">
          <div className="flex items-center gap-3">
            <Bell className="w-4 h-4 text-muted-foreground" strokeWidth={1.5} />
            <p className="text-sm">
              You have{' '}
              <strong>{stats.unread_notifications} unread notification{stats.unread_notifications !== 1 ? 's' : ''}</strong>
            </p>
          </div>
          <Link to="/dashboard/notifications" className="text-xs text-muted-foreground hover:text-foreground underline">
            View all →
          </Link>
        </div>
      )}
    </div>
  );
};

export default DashboardOverview;
